import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { environment } from '../../../environments/environment';
import { GoogleMapsLoaderService } from '../../service/google-maps-loader.service';

@Component({
  selector: 'app-events-map',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="events-map" #map></div>
    <p class="events-map-note" *ngIf="loaded && placed === 0">
      {{ 'weddings.map_empty' | translate }}
    </p>
  `,
  styleUrls: ['./events-map.component.css'],
})
export class EventsMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() events: any[] = [];
  @ViewChild('map') mapEl!: ElementRef<HTMLDivElement>;

  loaded = false;
  placed = 0;

  private map?: google.maps.Map;
  private geocoder?: google.maps.Geocoder;
  private markerClass?: typeof google.maps.marker.AdvancedMarkerElement;
  private clusterer?: MarkerClusterer;
  private geocodeCache = new Map<string, google.maps.LatLngLiteral>();
  private signature = '';

  constructor(
    private loader: GoogleMapsLoaderService,
    private router: Router,
  ) {}

  ngAfterViewInit() {
    this.loader
      .load()
      .then(() => this.initMap())
      .catch((err) => console.error('[EventsMap]', err));
  }

  ngOnChanges() {
    if (this.map) this.render();
  }

  ngOnDestroy() {
    this.clusterer?.clearMarkers();
  }

  private async initMap() {
    const marker: any = await (google.maps as any).importLibrary('marker');
    this.markerClass = marker.AdvancedMarkerElement;
    this.geocoder = new google.maps.Geocoder();
    this.map = new google.maps.Map(this.mapEl.nativeElement, {
      center: { lat: 41.9, lng: 12.5 },
      zoom: 5,
      mapId: environment.googleMapsMapId,
      mapTypeControl: false,
      streetViewControl: false,
    });
    this.clusterer = new MarkerClusterer({ map: this.map });
    this.loaded = true;
    this.signature = '';
    this.render();
  }

  private async render() {
    const sig = this.events.map((e) => e?._id).join(',');
    if (sig === this.signature) return;
    this.signature = sig;

    this.clusterer!.clearMarkers();
    const markers: google.maps.marker.AdvancedMarkerElement[] = [];
    const bounds = new google.maps.LatLngBounds();

    for (const ev of this.events) {
      const pos = await this.resolve(ev);
      if (!pos) continue;
      const marker = new this.markerClass!({
        position: pos,
        content: this.buildPin(ev),
      });
      markers.push(marker);
      bounds.extend(pos);
    }

    this.placed = markers.length;
    this.clusterer!.addMarkers(markers);

    if (markers.length > 0) {
      this.map!.fitBounds(bounds);
      if (markers.length === 1) this.map!.setZoom(13);
    }
  }

  private async resolve(ev: any): Promise<google.maps.LatLngLiteral | null> {
    const place = ev?.event?.location_place;
    if (place && place.lat != null && place.lng != null) {
      return { lat: place.lat, lng: place.lng };
    }
    const address = ev?.event?.location?.trim();
    if (!address) return null;
    if (this.geocodeCache.has(address)) return this.geocodeCache.get(address)!;
    try {
      const { results } = await this.geocoder!.geocode({ address });
      const loc = results?.[0]?.geometry?.location;
      if (!loc) return null;
      const literal = { lat: loc.lat(), lng: loc.lng() };
      this.geocodeCache.set(address, literal);
      return literal;
    } catch {
      return null;
    }
  }

  private buildPin(ev: any): HTMLElement {
    const past = this.isPast(ev);
    const pin = document.createElement('div');
    pin.className = `map-pin ${past ? 'map-pin--past' : 'map-pin--upcoming'}`;
    pin.addEventListener('click', () => this.onMarkerClick(ev));

    const name = document.createElement('span');
    name.className = 'map-pin__name';
    name.textContent = this.shortName(ev);
    pin.appendChild(name);

    const date = this.shortDate(ev);
    if (date) {
      const d = document.createElement('span');
      d.className = 'map-pin__date';
      d.textContent = date;
      pin.appendChild(d);
    }
    return pin;
  }

  private onMarkerClick(ev: any) {
    if (ev?._id && !ev?.is_collaboration) {
      this.router.navigate(['evento', ev._id]);
    }
  }

  private isPast(ev: any): boolean {
    const raw = ev?.event?.date;
    if (!raw) return false;
    const d = new Date(raw);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  }

  private shortName(ev: any): string {
    const first = ev?.client?.name ?? '';
    const last = ev?.client?.lastname ?? '';
    const initial = last ? ` ${last.charAt(0)}.` : '';
    return (first + initial).trim() || ev?.event?.location || '—';
  }

  private shortDate(ev: any): string {
    const raw = ev?.event?.date;
    if (!raw) return '';
    return new Date(raw).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
    });
  }
}
